import { showLoginModal } from 'controllers/login-modal-controller';
import {
  getAccessToken,
  notifySessionInvalid,
  refreshSession,
} from 'services/sessionRefresh';
import { ErroDaPergunta } from './erro-da-pergunta';

export { ErroDaPergunta };

/** Lê um stream de Server-Sent Events e entrega cada evento já com o JSON lido. */
async function lerEventos(corpo, aoEvento) {
  const leitor = corpo.getReader();
  const decodificador = new TextDecoder();
  let pendente = '';
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await leitor.read();
    if (done) break;
    pendente += decodificador.decode(value, { stream: true });
    const blocos = pendente.split('\n\n');
    pendente = blocos.pop();
    blocos.forEach((bloco) => {
      const evento = (bloco.match(/^event: (.+)$/m) || [])[1];
      const dados = (bloco.match(/^data: (.+)$/m) || [])[1];
      if (evento && dados) aoEvento(evento, JSON.parse(dados));
    });
  }
}

export class AgrogerIaFactory {
  constructor(instance) {
    this.instance = instance;
  }

  creditos() {
    return this.instance.get('/v1/agroger-ia/creditos');
  }

  conversas() {
    return this.instance.get('/v1/agroger-ia/conversas');
  }

  conversa(id) {
    return this.instance.get(`/v1/agroger-ia/conversas/${id}`);
  }

  renomear(id, titulo) {
    return this.instance.patch(`/v1/agroger-ia/conversas/${id}`, { titulo });
  }

  excluir(id) {
    return this.instance.delete(`/v1/agroger-ia/conversas/${id}`);
  }

  // Equipe Agroger: custo real, receita e margem por conta. A API recusa (403) para os outros.
  usoDaEquipe(mes) {
    return this.instance.get('/v1/agroger-ia/equipe/uso', { params: { mes } });
  }

  alterarPlano(conta, plano) {
    return this.instance.put(`/v1/agroger-ia/equipe/contas/${conta}/plano`, {
      plano,
    });
  }

  concederBonus(conta, creditos, motivo) {
    return this.instance.post(`/v1/agroger-ia/equipe/contas/${conta}/bonus`, {
      creditos,
      motivo,
    });
  }

  /**
   * A resposta chega aos pedaços (stream), então aqui é fetch e não axios.
   * Eventos: "consulta" (o que está sendo consultado), "texto", "fim" e "erro".
   * Recusas antes do stream (sem crédito, conversa de outro usuário) viram
   * ErroDaPergunta com o status e o tipo que a API mandou.
   */
  async perguntar(corpo, { aoEvento, sinal }) {
    const url = `${this.instance.defaults.baseURL.replace(/\/$/, '')}/v1/agroger-ia/perguntas`;
    const enviar = (token) =>
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(corpo),
        signal: sinal,
      });

    let resposta = await enviar(getAccessToken());

    // Token vencido: renova uma vez, como o axios faz nas outras telas.
    if (resposta.status === 401) {
      try {
        const tokens = await refreshSession(this.instance.defaults.baseURL);
        resposta = await enviar(tokens.accessToken);
      } catch (erro) {
        notifySessionInvalid({
          financial: false,
          error: erro,
          showLoginModal: true,
        });
        showLoginModal();
        throw new ErroDaPergunta(
          'Sua sessão expirou. Entre de novo para continuar.',
          { status: 401 },
        );
      }
    }

    if (!resposta.ok || !resposta.body) {
      const json = await resposta.json().catch(() => ({}));
      throw new ErroDaPergunta(
        json.error ||
          'Não consegui responder agora. Tente de novo em instantes.',
        {
          status: resposta.status,
          tipo: json.type,
        },
      );
    }

    await lerEventos(resposta.body, aoEvento);
  }

  obterHabilitacaoWhatsapp(requestId) {
    return this.instance.get(
      `/v1/agroger-ia/whatsapp/habilitacao/${requestId}`,
    );
  }

  decidirHabilitacaoWhatsapp(requestId, aprovar) {
    return this.instance.post(
      `/v1/agroger-ia/whatsapp/habilitacao/${requestId}/decidir`,
      { aprovar },
    );
  }
}

export default AgrogerIaFactory;
